import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Owner, OWNER_ROLES, OWNER_STATUS } from '~/types';
import { useLazyQuery } from '@apollo/client';
import { GET_OWNER } from '~/services/auth';
import { useTranslation } from 'react-i18next';

export interface OwnerResponse extends Partial<Owner> {
  _id: string;
  email: string;
  agents: { name: string; _id: string; role: OWNER_ROLES; status: string }[];
  language?: string;
  person_id?: string;
  first_name?: string;
  last_name?: string;
  background_color?: string;
  primary_phone?: string;
  primary_email?: string;
  allow_referral?: boolean;
  referral_code?: string;
}

type AuthContextProps = {
  owner: OwnerResponse;
  createSession: (response: any) => void;
  getOwner: () => void;
  setDefaultOwner: () => void;
  loading: boolean;
};

export const SessionContext = createContext<AuthContextProps>({
  owner: {
    _id: '',
    email: '',
    status: OWNER_STATUS.PENDING,
    person_id: '',
    agents: [],
  },
  createSession: () => {},
  setDefaultOwner: () => {},
  getOwner: () => {},
  loading: true,
});

const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [owner, setOwner] = useState<OwnerResponse>({
    _id: '',
    email: '',
    status: OWNER_STATUS.PENDING,
    person_id: '',
    agents: [],
    first_name: '',
    last_name: '',
    background_color: '',
    primary_phone: '',
    primary_email: '',
  });
  const [loading, setLoading] = useState(true);
  const [query] = useLazyQuery(GET_OWNER);
  const { i18n } = useTranslation();

  const createSession = (response: any) => {
    const accessToken = response?.response?.data?.accessToken;
    const refreshToken = response?.response?.data?.refreshToken;
    if (!accessToken) return;
    // Persist tokens first so authenticated queries succeed
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    // Ensure app shows loader while fetching owner
    setLoading(true);
    // Fetch owner from API (authoritative source)
    getOwner();
  };

  const getOwner = async () => {
    try {
      const res = await query({
        fetchPolicy: 'no-cache',
      });
      const apiResponse = res?.data?.response;
      const ownerData = apiResponse?.data as OwnerResponse | undefined;

      if (!apiResponse || apiResponse.hasError || !ownerData?._id) {
        // Invalid/expired tokens or unable to fetch owner: clear session
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setDefaultOwner();
        setLoading(false);
        return;
      }

      setOwner(ownerData);
      if (ownerData.language) {
        i18n.changeLanguage(ownerData.language);
      }
      setLoading(false);
    } catch (err) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setDefaultOwner();
      setLoading(false);
    }
  };

  const setDefaultOwner = () => {
    setOwner({
      _id: '',
      email: '',
      status: OWNER_STATUS.PENDING,
      person_id: '',
      agents: [],
    });
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && accessToken !== 'undefined') {
      getOwner();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <SessionContext.Provider
      value={{ getOwner, loading, owner, createSession, setDefaultOwner }}
    >
      {children}
    </SessionContext.Provider>
  );
};

const useSession = () => useContext(SessionContext);

export { SessionProvider, useSession };
